/**
 * More per-stage textures and samplers than WebGPU's default 16 where the GPU offers them (the ground material
 * samples two textures per ground set; see logic/textureBudget.ts, which degrades explicitly when they do not fit).
 *
 * Asked of the same adapter three.js requests (a wrapper on the next navigator.gpu.requestAdapter call), capped at
 * 32 and never above the adapter's own limits; skipped on software adapters (SwiftShader hung at 20 % loading when
 * a second adapter was queried for its limits); a rejected device request falls back to the defaults. ?nolimits=1
 * turns it off.
 */
type Adapter = { limits: Record<string, number>; info?: { vendor?: string; architecture?: string }; isFallbackAdapter?: boolean; requestDevice(d?: { requiredLimits?: Record<string, number> } & Record<string, unknown>): Promise<unknown> };
type Gpu = { requestAdapter(o?: object): Promise<Adapter | null> };

const KEYS = ['maxSampledTexturesPerShaderStage', 'maxSamplersPerShaderStage'];

export function raiseTextureLimits(): void {
  const gpu = (navigator as unknown as { gpu?: Gpu }).gpu;
  if (!gpu || new URLSearchParams(location.search).get('nolimits') === '1') return;
  const request = gpu.requestAdapter.bind(gpu);
  gpu.requestAdapter = async (o?: object) => {
    gpu.requestAdapter = request; // once: three's own request
    const a = await request(o);
    if (!a || a.isFallbackAdapter || /swiftshader/i.test(`${a.info?.vendor} ${a.info?.architecture}`)) return a;
    const extra: Record<string, number> = {};
    for (const k of KEYS) { const v = Math.min(32, a.limits[k] ?? 16); if (v > 16) extra[k] = v; }
    if (!Object.keys(extra).length) return a;
    const requestDevice = a.requestDevice.bind(a);
    a.requestDevice = async (d = {}) => {
      try { return await requestDevice({ ...d, requiredLimits: { ...d.requiredLimits, ...extra } }); } catch { return requestDevice(d); }
    };
    return a;
  };
}

/** The limit the ground material must fit (the device's, or 16 on WebGL2 / unknown). */
export function deviceTextureLimit(renderer: { backend: unknown }): number {
  const forced = Number(new URLSearchParams(location.search).get('texlimit')); // QA: ?texlimit=16 exercises the fallback
  if (forced > 0) return forced;
  const d = (renderer.backend as { device?: { limits: { maxSampledTexturesPerShaderStage: number; maxSamplersPerShaderStage: number } } }).device;
  return d ? Math.min(d.limits.maxSampledTexturesPerShaderStage, d.limits.maxSamplersPerShaderStage) : 16;
}
